import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { buildAuroraDataset } from '@aurora-pvi/seed';
import { createFixtureRepositories, type Repositories } from '@aurora-pvi/testing';
import { useSession } from './SessionProvider';
import { createHttpPortfolioRepository } from '../../api/httpPortfolioRepository';
import { createHttpProductRepository } from '../../api/httpProductRepository';
import { createHttpDeliveryRepository } from '../../api/httpDeliveryRepository';
import { createHttpInvestmentRepository } from '../../api/httpInvestmentRepository';
import { createHttpApplicationRepository } from '../../api/httpApplicationRepository';
import { createHttpIdentityRepository } from '../../api/httpIdentityRepository';
import { createHttpOutcomeRepository } from '../../api/httpOutcomeRepository';
import { createHttpEstateRepository } from '../../api/httpEstateRepository';
import { createHttpMappingRepository } from '../../api/httpMappingRepository';
import { createHttpDiscoverRepository } from '../../api/httpDiscoverRepository';
import { createHttpIntegrationRepository } from '../../api/httpIntegrationRepository';
import { createHttpPlanningRepository } from '../../api/httpPlanningRepository';
import { createHttpCollaborationRepository } from '../../api/httpCollaborationRepository';
import { createHttpGovernanceRepository } from '../../api/httpGovernanceRepository';
import { createHttpProcessRepository } from '../../api/httpProcessRepository';

/**
 * The swap point.
 *
 * Every screen reaches data through this context and nothing else. Six
 * methods are actually swapped so far: `portfolio.getSummary`,
 * `products.list`, `products.get360`, `delivery.getOverview`,
 * `value.getInvestment` and `applications.list`, each only for the default (unfiltered,
 * current-period) view — when `VITE_API_BASE_URL` is set, those calls are
 * answered by the real API (apps/api, reading Postgres) instead of the
 * fixture. Neither `PortfolioGlassPage`, `ProductListGlassPage`,
 * `Product360GlassPage`, the delivery screen nor the investment screen
 * changes to make this true; each only ever called
 * `repositories.<group>.<method>(filters)`, which is the whole argument of
 * the architecture and the thing this file is where it is either honoured or
 * quietly broken.
 *
 * Swapped methods answer *every* call, filtered or not. They used not to:
 * a narrowed request fell back to the fixture, because the routes accepted no
 * filters and substituting "the whole tenant" for "what was actually asked"
 * would have been worse than having no real data. The routes take filters now
 * (apps/api/src/filters.ts), so the fallback is gone — choosing a filter no
 * longer changes where the numbers come from.
 *
 * Every group and method not listed below still reads the fixture, because the
 * API does not implement it yet.
 *
 * The rule (Prompt 3.16): a page must not be able to tell whether its rows came
 * from a spreadsheet, a fixture, PostgreSQL or a future connector. If you ever
 * find yourself wanting to import a repository directly into a screen, that is
 * the signal the boundary is about to be violated.
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

const DataContext = createContext<Repositories | null>(null);

interface DataProviderProps {
  children: ReactNode;
  /** Injected in tests to pin a scenario without rebuilding the dataset. */
  repositories?: Repositories;
}

export function DataProvider({ children, repositories }: DataProviderProps) {
  const { currentUser } = useSession();

  const value = useMemo(() => {
    if (repositories) return repositories;
    // Deterministic seed: the same dataset every reload, so a screenshot taken
    // today matches the one taken last week and visual regression is meaningful.
    // It is also the same seed apps/api/src/scripts/load-seed-data.ts loads
    // into Postgres, so the fixture and the real backend describe one airline.
    const dataset = buildAuroraDataset(42);
    const fixtures = createFixtureRepositories(dataset, currentUser);
    if (!API_BASE_URL) return fixtures;

    const httpPortfolio = createHttpPortfolioRepository(API_BASE_URL);
    const httpProducts = createHttpProductRepository(API_BASE_URL);
    const httpDelivery = createHttpDeliveryRepository(API_BASE_URL);
    const httpInvestment = createHttpInvestmentRepository(API_BASE_URL);
    const httpApplications = createHttpApplicationRepository(API_BASE_URL);
    const httpIdentity = createHttpIdentityRepository(API_BASE_URL);
    const httpOutcomes = createHttpOutcomeRepository(API_BASE_URL);
    const httpEstate = createHttpEstateRepository(API_BASE_URL);
    const httpMapping = createHttpMappingRepository(API_BASE_URL);
    const httpDiscover = createHttpDiscoverRepository(API_BASE_URL);
    const httpIntegrations = createHttpIntegrationRepository(API_BASE_URL);
    const httpPlanning = createHttpPlanningRepository(API_BASE_URL);
    const httpCollaboration = createHttpCollaborationRepository(API_BASE_URL);
    const httpGovernance = createHttpGovernanceRepository(API_BASE_URL);
    const httpProcess = createHttpProcessRepository(API_BASE_URL);
    return {
      ...fixtures,
      portfolio: {
        ...fixtures.portfolio,
        getSummary: httpPortfolio.getSummary,
      },
      products: {
        ...fixtures.products,
        list: httpProducts.list,
        get360: httpProducts.get360,
      },
      delivery: {
        ...fixtures.delivery,
        getOverview: httpDelivery.getOverview,
      },
      applications: {
        ...fixtures.applications,
        list: httpApplications.list,
      },
      /*
        Whole-group. `iam.role`, `iam.person_role`, `iam.role_permission`,
        `iam.access_scope` and `iam.field_policy` between them hold 309 rows
        that decide what everybody in this product may see, and three screens
        described them from a fixture — including the permissions matrix, which
        is the one screen in the product whose entire subject is what the
        database enforces.
      */
      identity: {
        ...fixtures.identity,
        getDelegation: () => httpIdentity.getDelegation(),
        getRolesAccess: () => httpIdentity.getRolesAccess(),
        getProfile: () => httpIdentity.getProfile(),
        getSignIn: () => httpIdentity.getSignIn(),
      },
      value: {
        ...fixtures.value,
        getInvestment: httpInvestment.getInvestment,
      },
      outcomes: {
        ...fixtures.outcomes,
        getOutcomeTraceability: httpOutcomes.getOutcomeTraceability,
      },
      /*
        Scenarios stop being authored.

        `scenario.scenario`, its assumptions and its results held zero rows for
        the life of the schema, so this screen read 2,000 lines of fixture and
        "recalculate" returned the same numbers it had returned before. The
        model now lives in the route (apps/api/src/routes/scenarios.ts) and this
        is the swap that makes the screen see it.
      */
      scenarios: {
        ...fixtures.scenarios,
        compare: httpPlanning.compare,
      },
      discover: {
        ...fixtures.discover,
        // `getResult` is deliberately not swapped. The results screen asks for
        // a boundary register, an included-process list and an action queue
        // that nothing in the database records yet, and serving it half-real
        // would put invented rows behind the screen that argues hardest that
        // its rows are governed.
        listCandidates: httpDiscover.listCandidates,
        getWizard: httpDiscover.getWizard,
      },
      /*
        Whole-group now. The process studio was the last method on it reading a
        fixture, and it was the one that mattered most: 3,228 governed edges
        existed in the database while the screen drew a generated graph, so the
        studio disagreed with the product record it was supposed to be editing.
      */
      mapping: {
        ...fixtures.mapping,
        getApplicationMapping: httpMapping.getApplicationMapping,
        getProcessMapping: httpMapping.getProcessMapping,
      },
      integrations: {
        ...fixtures.integrations,
        // Whole-group, unusually, because the group has one method and it is
        // now served entirely from `ingest.connector` and `ingest.ingestion_run`.
        // A connector health screen reading a fixture reported the health of
        // connectors that did not exist.
        getOperations: () => httpIntegrations.getOperations(),
      },
      lineage: {
        ...fixtures.lineage,
        getWorkbench: (datasetId?: string) => httpIntegrations.getWorkbench(datasetId),
      },
      collaboration: {
        ...fixtures.collaboration,
        /*
          The two screens whose rows are now written by people using the
          product. `getReports` and `getTaxonomyGovernance` still read the
          fixture and are tracked separately — swapping one method at a time is
          what makes that visible rather than hiding it behind a whole-group
          replacement.
        */
        getCollaboration: httpCollaboration.getCollaboration,
        getWatchlist: () => httpCollaboration.getWatchlist(),
      },
      /*
        The governance console. It counted a fixture's queues while
        `gov.approval` held 75 rows, `gov.decision` 58 and `gov.audit_event`
        377 — a console reporting on work nobody was doing, beside a queue full
        of work nobody was being told about.
      */
      governance: {
        ...fixtures.governance,
        getCommandCenter: httpGovernance.getCommandCenter,
      },
      /*
        Whole-group. The heatmap was the last of the three: it drew a
        modernization grid from a fixture while `core.application` held 1,924
        rows carrying the lifecycle and the technical health score the grid is
        made of.
      */
      /*
        The catalogue and one process's record. `core.process` held 71 rows for
        the life of the schema and nothing read them, so both screens described
        a taxonomy that existed nowhere else in the product — including on the
        mapping studio next door, which was drawing edges to processes these two
        screens had never heard of.
      */
      process: {
        ...fixtures.process,
        getCatalog: httpProcess.getCatalog,
        getDetail: httpProcess.getDetail,
      },
      estate: {
        ...fixtures.estate,
        getDuplicateReview: httpEstate.getDuplicateReview,
        comparePortfolio: httpEstate.comparePortfolio,
        getTechnologyHeatmap: httpEstate.getTechnologyHeatmap,
      },
    };
  }, [repositories, currentUser]);

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

export function useRepositories(): Repositories {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useRepositories must be used within a <DataProvider>.');
  }
  return context;
}
